// AIMS PWA - "Sync now" button (AIMS-Offline-Inspection.md §8/§198).
//
// Manual trigger for the reconcile loop, in ADDITION to auto-sync (launch +
// `online`). Live states:
//   • offline  → disabled "Offline - connect to sync"
//   • idle     → "Sync now" (+ pending-count badge)
//   • syncing  → "Syncing… X of Y" spinner
//   • done     → "✓ Synced" + last-synced timestamp
// Safe to spam - the engine coalesces concurrent runs and pushes are idempotent.

import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import { runSync } from '../../offline/sync';

export default function SyncButton({ online, pendingCount = 0, onSynced }) {
  const [state, setState] = useState('idle'); // idle | syncing | done
  const [progress, setProgress] = useState(null); // { done, total }
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [note, setNote] = useState(null);

  const doSync = async () => {
    if (!online || state === 'syncing') return;
    setState('syncing'); setNote(null); setProgress(null);
    const summary = await runSync({
      onProgress: (p) => { if (p.phase === 'push' || p.phase === 'pull') setProgress({ done: p.done, total: p.total }); },
    });
    if (summary.needsReauth) {
      setNote('Session expired - log in again to sync (your work is safe).');
      setState('idle');
    } else if (summary.error === 'offline') {
      setNote('Still offline - connect and try again.');
      setState('idle');
    } else {
      setLastSyncAt(Date.now());
      setState('done');
      const notes = [];
      if (summary.failed) notes.push(`${summary.failed} item${summary.failed === 1 ? '' : 's'} could not upload - will retry.`);
      if (summary.clearedVisits && summary.clearedVisits.length) {
        notes.push(summary.clearedVisits
          .map((v) => v.visitDeleted
            ? `Visit #${v.visitId} was deleted at HQ; your inspections were uploaded and the offline copy removed.`
            : `Visit #${v.visitId} checkout was cleared${v.clearedBy ? ' by ' + v.clearedBy : ''}; your inspections were uploaded and the offline copy removed.`)
          .join(' '));
      }
      if (notes.length) setNote(notes.join(' '));
    }
    if (onSynced) onSynced(summary);
  };

  let label;
  if (!online) label = 'Offline - connect to sync';
  else if (state === 'syncing') label = progress && progress.total > 1 ? `Syncing… ${progress.done} of ${progress.total}` : 'Syncing…';
  else if (state === 'done') label = 'Synced';
  else label = 'Sync now';

  const icon = state === 'syncing'
    ? <CircularProgress size={16} color="inherit" />
    : state === 'done'
      ? <CheckCircleIcon fontSize="small" />
      : <SyncIcon fontSize="small" />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
      <Badge badgeContent={online && state !== 'syncing' && pendingCount > 0 ? pendingCount : 0} color="warning">
        <Button
          size="small"
          variant={state === 'done' ? 'outlined' : 'contained'}
          color={state === 'done' ? 'success' : 'primary'}
          disabled={!online || state === 'syncing'}
          onClick={doSync}
          startIcon={icon}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {label}
        </Button>
      </Badge>
      {state === 'done' && lastSyncAt && !note && (
        <Box sx={{ fontSize: 10.5, color: 'var(--muted)' }}>Last synced {dayjs(lastSyncAt).format('HH:mm')}</Box>
      )}
      {note && <Box sx={{ fontSize: 10.5, color: 'var(--muted)', maxWidth: 220, textAlign: 'right' }}>{note}</Box>}
    </Box>
  );
}
